"use client";

import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import Icon from "@/components/ui/AppIcon";
import { FORMSUBMIT_EMAIL, GOOGLE_SHEETS_URL, submitForm, openWhatsAppFallback } from "@/lib/formSubmit";

const WINDOW_FLAG_KEY = "__hayat_home_popup_shown__";

const INITIAL_FORM = {
  fullName: "",
  phone: "",
  location: "",
};

const phoneRegex = /^[6-9]\d{9}$/;

const sanitizePhone = (value: string) => value.replace(/\D/g, "").slice(0, 10);

interface LeadCapturePopupProps {
  delayMs?: number;
  autoTrigger?: boolean; // enable timed popup when true
}

export default function LeadCapturePopup({ delayMs = 4500, autoTrigger = true }: LeadCapturePopupProps) {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false); // controls animation state
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const timerRef = useRef<number>();
  const transitionMs = 300; // should match CSS transition duration
  const minSubmittingMs = 700; // ensure spinner is visible for at least this long
  const successVisibleMs = 5000; // how long the success message stays visible (ms)

  useEffect(() => {
    if (!autoTrigger) return;
    if (typeof window === "undefined") return;

    if ((window as any)[WINDOW_FLAG_KEY]) {
      return;
    }

    timerRef.current = window.setTimeout(() => {
      // mount -> then open (so we can animate)
      setVisible(true);
      // wait a frame then enable the open class for animation
      requestAnimationFrame(() => setOpen(true));
      (window as any)[WINDOW_FLAG_KEY] = true;
    }, delayMs);

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, [delayMs, autoTrigger]);
  useEffect(() => {
    const openHandler = (e: Event) => {
      // If dispatcher provided a detail object, mark handled so callers can detect presence
      try {
        const ce = e as CustomEvent<{ handled?: boolean }>;
        if (ce && ce.detail && typeof ce.detail === 'object') {
          ce.detail.handled = true;
        }
      } catch (err) {
        // ignore
      }

      // show popup immediately and animate open
      setVisible(true);
      requestAnimationFrame(() => setOpen(true));
      // mark as shown so the timed popup won't re-trigger unnecessarily
      if (typeof window !== 'undefined') (window as any)[WINDOW_FLAG_KEY] = true;
    };

    window.addEventListener('hayat:open-lead-popup', openHandler as EventListener);

    return () => {
      window.removeEventListener('hayat:open-lead-popup', openHandler as EventListener);
    };
  }, []);
  const closePopup = () => {
    // animate out first
    setOpen(false);

    // delay unmount so the exit animation can run
    setTimeout(() => {
      setVisible(false);
    }, transitionMs);

    if (typeof window !== "undefined") {
      (window as any)[WINDOW_FLAG_KEY] = true;
    }
  };

  const handleChange = (field: keyof typeof INITIAL_FORM) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = field === "phone" ? sanitizePhone(event.target.value) : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!formData.fullName.trim()) {
      setError("Please add your name.");
      return;
    }

    if (!phoneRegex.test(formData.phone)) {
      setError("Add a valid 10-digit mobile number.");
      return;
    }

    // show submitting state
    setIsSubmitting(true);
    const submitStart = Date.now();

    const payload = {
      ...formData,
      projectType: "Quick consultation",
      source: "Homepage timed popup",
      formVariant: "homepage_popup",
      pagePath: typeof window !== "undefined" ? window.location.pathname : "/",
    };

    const result = await submitForm(payload, FORMSUBMIT_EMAIL, GOOGLE_SHEETS_URL);

    // ensure spinner shows at least a short amount of time for good UX
    const elapsed = Date.now() - submitStart;
    const remaining = Math.max(0, minSubmittingMs - elapsed);
    if (remaining > 0) await new Promise((res) => setTimeout(res, remaining));

    setIsSubmitting(false);

    if (result.success) {
      // show success state (keep the popup visually open while success is visible)
      setSubmitted(true);
      setFormData(INITIAL_FORM);
      setError(null);

      // keep success visible for configured duration, then animate out
      setTimeout(() => {
        setOpen(false);
        setTimeout(() => setVisible(false), transitionMs);
      }, successVisibleMs);
    } else {
      setError(result.error || "Could not submit. Please try again.");
      
      // Fallback: Open WhatsApp with the form details
      // We wrap it in a small timeout to ensure the error message is visible for a moment
      // and to avoid blocking the main UI thread.
      setTimeout(() => {
        openWhatsAppFallback(payload);
      }, 1500);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center p-4 sm:p-6 pb-6 sm:pb-10 pointer-events-none">
      <div
        className={`pointer-events-auto w-[min(420px,95vw)] rounded-3xl border shadow-2xl relative overflow-hidden transform transition-all duration-300 ease-out ${
          submitted ? "bg-emerald-600 text-white border-emerald-500/70" : "bg-white text-slate-900 border-slate-200/80"
        } ${open ? "translate-y-0 opacity-100 scale-100" : "translate-y-6 opacity-0 scale-95"}`}
        aria-live="polite"
      >
        <div
          className={`absolute inset-0 pointer-events-none z-0 bg-gradient-to-br ${
            submitted
              ? "from-emerald-900 via-emerald-900/80 to-emerald-800"
              : "from-emerald-500/10 via-white/40 to-white"
          }`}
          aria-hidden
        />

        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); closePopup(); }}
          className={`pointer-events-auto z-50 absolute right-3 top-3 flex items-center justify-center rounded-full p-2 transition hover:scale-105 cursor-pointer ${
            submitted ? "bg-white/20 text-white" : "bg-white text-slate-700 shadow-sm"
          }`}
          aria-label="Close quick contact form"
        >
          <Icon name="XMarkIcon" size={18} />
        </button>

        <div className="relative z-10 p-5 sm:p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500 text-white shadow-lg shadow-emerald-500/30">
              <Icon name="SparklesIcon" variant="solid" size={22} />
            </div>
            <div className="space-y-0.5">
              <p className={`text-xs font-semibold uppercase tracking-[0.18em] ${submitted ? "text-white/80" : "text-emerald-700"}`}>
                Priority callback
              </p>
              <h3 className={`text-xl sm:text-2xl font-headline font-extrabold tracking-tight ${submitted ? "text-white" : "text-slate-900"}`}>
                Book a 15-min consult
              </h3>
            </div>
          </div>

          {submitted ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-white">
                <Icon name="CheckCircleIcon" variant="solid" size={22} />
                <p className="font-semibold">Got it! Your request is in.</p>
              </div>
              <p className="text-sm text-white/90">
                A designer will call you shortly to understand your space and next steps.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Name</span>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={handleChange("fullName")}
                    className="w-full rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 text-slate-900 shadow-inner shadow-slate-100 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    placeholder="Hey, I'm Ananya"
                  />
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Phone</span>
                  <div className="flex items-center gap-2 rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 shadow-inner shadow-slate-100 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-100">
                    <span className="text-sm text-slate-500">+91</span>
                    <input
                      type="tel"
                      inputMode="tel"
                      value={formData.phone}
                      onChange={handleChange("phone")}
                      className="w-full bg-transparent text-slate-900 outline-none"
                      placeholder="98765 43210"
                    />
                  </div>
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">City / Area</span>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={handleChange("location")}
                    className="w-full rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 text-slate-900 shadow-inner shadow-slate-100 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    placeholder="HSR, Whitefield, Indiranagar..."
                  />
                </label>
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <div className="mt-3">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-4 py-3 text-white shadow-lg shadow-emerald-400/40 transition hover:-translate-y-px hover:shadow-emerald-500/40 focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isSubmitting ? (
                    <>
                      <span className="h-2 w-2 animate-ping rounded-full bg-white" />
                      <span className="text-sm">Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span className="text-sm font-semibold">Submit</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
